'use client';

import { GlassCard, V4CtaButton } from '@/components/landing/v4-shared';

interface PartnersSignupFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void> | void;
  submitting: boolean;
  submitStatus: 'idle' | 'success' | 'error';
  errorMessage: string;
}

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: 'rgba(244,244,240,0.04)',
  border: '1px solid var(--hair-2)',
  borderRadius: 12,
  color: 'var(--paper)',
  fontFamily: 'var(--font-geist-sans)',
  fontSize: 15,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-geist-mono)',
  fontSize: 11,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--dim)',
  margin: '0 0 6px',
};

export function PartnersSignupForm({
  onSubmit,
  submitting,
  submitStatus,
  errorMessage,
}: PartnersSignupFormProps) {
  if (submitStatus === 'success') {
    return (
      <GlassCard accent="cyan" padding={32} radius={20} className="mt-10">
        <div style={{ textAlign: 'center' }}>
          <div
            aria-hidden="true"
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              background: 'rgba(125,250,255,0.18)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--cyan-glow)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 22,
              fontWeight: 500,
              color: 'var(--paper)',
              margin: '0 0 8px',
            }}
          >
            Application submitted.
          </h3>
          <p style={{ color: 'var(--dim)', fontSize: 14, margin: 0 }}>
            We will review your application and get back to you within 24 hours.
          </p>
        </div>
      </GlassCard>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-10" style={{ display: 'grid', gap: 16 }}>
      {submitStatus === 'error' && (
        <p
          role="alert"
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid rgba(239,68,68,0.4)',
            background: 'rgba(239,68,68,0.08)',
            color: 'rgb(252,165,165)',
            fontSize: 13,
            margin: 0,
          }}
        >
          {errorMessage}
        </p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label htmlFor="first_name" style={labelStyle}>
            First Name
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            required
            placeholder="Jane"
            style={inputBaseStyle}
          />
        </div>
        <div>
          <label htmlFor="last_name" style={labelStyle}>
            Last Name
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            required
            placeholder="Smith"
            style={inputBaseStyle}
          />
        </div>
      </div>
      <div>
        <label htmlFor="email" style={labelStyle}>
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          placeholder="jane@example.com"
          style={inputBaseStyle}
        />
      </div>
      <div>
        <label htmlFor="company" style={labelStyle}>
          Company
        </label>
        <input
          type="text"
          id="company"
          name="company"
          placeholder="Smith and Associates CPA"
          style={inputBaseStyle}
        />
      </div>
      <div>
        <label htmlFor="role" style={labelStyle}>
          Role
        </label>
        <select id="role" name="role" required style={inputBaseStyle}>
          <option value="">Select your role</option>
          <option value="cpa">CPA / Accountant</option>
          <option value="insurance">Insurance Broker</option>
          <option value="consultant">Business Consultant</option>
          <option value="other">Other</option>
        </select>
      </div>
      <V4CtaButton type="submit" size="lg">
        {submitting ? 'Submitting…' : 'Apply Now'}
      </V4CtaButton>
      <p
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--dimmer)',
          margin: 0,
        }}
      >
        By applying you agree to our Partner Terms.
      </p>
    </form>
  );
}
